  import React, { useState, useEffect } from 'react';
  import { useNavigate } from 'react-router-dom';
  import { useForm } from 'react-hook-form';
  import { useAuth } from '../../../contexts/AuthContext';
  import { useNotifications } from '../../../contexts/NotificationContext';
  import {
    Mail,
    Lock,
    User,
    Building,
    Phone,
    AlertCircle,
    CheckCircle,
    Globe,
    Upload,
    Briefcase,
    MapPin,
    FileText,
    Shield
  } from 'lucide-react';
  import Button from '../../../components/common/Button';
  import Input from '../../../components/common/Input';
  import Select from '../../../components/common/Select';
 import logo from "../../../assets/styles/crossdeal Transparent1.png"
 
  const SignupPage = () => {
    const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm();
    const [loading, setLoading] = useState(false);
    const [signupError, setSignupError] = useState('');
    const [role, setRole] = useState('buyer');
    const [companyLogo, setCompanyLogo] = useState(null);
    const [licenseImage, setLicenseImage] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [showSellerFields, setShowSellerFields] = useState(false);
    const [registrationComplete, setRegistrationComplete] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});

    const navigate = useNavigate();
    const { register: registerUser, user, isAuthenticated, processingAuth } = useAuth();
    const { addNotification } = useNotifications();

    const password = watch('password', '');

    useEffect(() => {
      if (isAuthenticated && user && !processingAuth) {
        const redirectPath = user.role === 'seller' ? '/seller/dashboard' : '/buyer/dashboard';
        navigate(redirectPath, { replace: true });
      }
    }, [isAuthenticated, user, navigate, processingAuth]);

    useEffect(() => {
      if (registrationComplete && user) {
        addNotification({
          type: 'success',
          title: 'Registration Complete!',
          message: 'Account created successfully. Redirecting to dashboard...'
        });

        const timer = setTimeout(() => {
          if (user.role) {
            const redirectPath = user.role === 'seller' ? '/seller/dashboard' : '/buyer/dashboard';
            navigate(redirectPath, { replace: true });
          }
        }, 1500);

        return () => clearTimeout(timer);
      }
    }, [registrationComplete, user, navigate, addNotification]);

    const countries = [
      { code: 'US', name: 'United States', dialCode: '+1' },
      { code: 'IN', name: 'India', dialCode: '+91' },
      { code: 'GB', name: 'United Kingdom', dialCode: '+44' },
      { code: 'AE', name: 'United Arab Emirates', dialCode: '+971' },
      { code: 'SG', name: 'Singapore', dialCode: '+65' },
      { code: 'CA', name: 'Canada', dialCode: '+1' },
      { code: 'AU', name: 'Australia', dialCode: '+61' },
      { code: 'DE', name: 'Germany', dialCode: '+49' },
      { code: 'FR', name: 'France', dialCode: '+33' },
      { code: 'JP', name: 'Japan', dialCode: '+81' },
    ];

    const businessTypes = [
      { id: 1, name: 'Manufacturer' },
      { id: 2, name: 'Exporter' },
      { id: 3, name: 'Importer' },
      { id: 4, name: 'Trader' },
      { id: 5, name: 'Distributor' },
      { id: 6, name: 'Wholesaler' },
      { id: 7, name: 'Retailer' },
      { id: 8, name: 'Service Provider' },
      { id: 9, name: 'Consultant' },
    ];

    const handleRoleChange = (newRole) => {
      setRole(newRole);
      setShowSellerFields(newRole === 'seller');
      // Clear seller-specific fields if switching to buyer
      if (newRole !== 'seller') {
        setValue('companyName', '');
        setValue('companyDescription', '');
        setValue('businessType', '');
        setValue('GSTNumber', '');
        setValue('licenseNumber', '');
        setCompanyLogo(null);
        setLicenseImage(null);
      }
    };

    const handleFileUpload = async (e, type = 'logo') => {
      const file = e.target.files[0];
      if (!file) return;

      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const validDocumentTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

      if (type === 'logo' || type === 'license') {
        if (!validImageTypes.includes(file.type)) {
          setSignupError('Please upload a valid image file (JPG, PNG, GIF, WEBP)');
          return;
        }
        if (file.size > 2 * 1024 * 1024) { // 2MB
          setSignupError('Image size should be less than 2MB');
          return;
        }
      } else if (type === 'document') {
        if (!validDocumentTypes.includes(file.type)) {
          setSignupError('Please upload a valid document (JPG, PNG, PDF)');
          return;
        }
        if (file.size > 5 * 1024 * 1024) { // 5MB
          setSignupError('Document size should be less than 5MB');
          return;
        }
      }

      // Show upload progress
      setUploadProgress(prev => ({
        ...prev,
        [type]: { progress: 0, fileName: file.name }
      }));

      try {
        // Set the file immediately for form display
        switch (type) {
          case 'logo':
            setCompanyLogo(file);
            break;
          case 'license':
            setLicenseImage(file);
            break;
          case 'document':
            if (attachments.length < 5) {
              setAttachments(prev => [...prev, file]);
            } else {
              setSignupError('Maximum 5 attachments allowed');
            }
            break;
        }

        // Clear progress after 1 second
        setTimeout(() => {
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[type];
            return newProgress;
          });
        }, 1000);

      } catch (error) {
        setSignupError(`Failed to upload ${type}: ${error.message}`);
      }
    };

    const removeAttachment = (index) => {
      setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const removeFile = (type) => {
      if (type === 'logo') {
        setCompanyLogo(null);
      } else if (type === 'license') {
        setLicenseImage(null);
      }
    };

    const onSubmit = async (data) => {
      setLoading(true);
      setSignupError('');
      setRegistrationComplete(false);

      try {
        // Prepare user data with files
        const userData = {
          name: data.name,
          email: data.email,
          password: data.password,
          phone: data.phone,
          role: role, // This should be 'buyer' or 'seller'
          country: data.country,
          countryCode: data.countryCode || '+1',
          state: data.state,
          city: data.city,
          postalCode: data.postalCode,
          address: data.address,

          // Seller-specific fields (only include if role is 'seller')
          ...(role === 'seller' && {
            companyName: data.companyName,
            companyDescription: data.companyDescription,
            businessType: data.businessType,
            GSTNumber: data.GSTNumber || '',
            licenseNumber: data.licenseNumber || '',
            companyLogo: companyLogo,
            licenseImage: licenseImage,
            port: data.port || '',
            loadingPort: data.loadingPort || '',
            destinationPort: data.destinationPort || ''
          }),

          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          attachments: attachments
        };

        const result = await registerUser(userData);

        if (result.success) {
          setRegistrationComplete(true);
          // Reset form after successful registration
          reset();
          setCompanyLogo(null);
          setLicenseImage(null);
          setAttachments([]);
        } else {
          setSignupError(result.message);
          addNotification({
            type: 'error',
            title: 'Registration Failed',
            message: result.message
          });
          setLoading(false);
          setRegistrationComplete(false);
        }
      } catch (error) {
        setSignupError(error.message || 'Registration failed. Please try again.');
        addNotification({
          type: 'error',
          title: 'Registration Error',
          message: error.message || 'Registration failed'
        });
        setLoading(false);
        setRegistrationComplete(false);
      }
    };

    if (processingAuth && registrationComplete) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-lg font-semibold text-gray-900">Setting up your account...</p>
            <p className="mt-2 text-gray-600">Please wait while we configure your dashboard.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
        <div className="w-full max-w-4xl">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <img 
                        src={logo} 
                        alt="CrossDeal Logo" 
                        className="h-15 w-16 object-contain"
                      />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Create Your Business Account
            </h1>
            <p className="mt-1 text-gray-600">
              Join our platform to connect with global business partners
            </p>
          </div>

          <div className="bg-white py-6 px-5 shadow-lg rounded-2xl sm:px-6 border border-gray-200">
            {signupError && (
              <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="h-4 w-4 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Registration Error</p>
                    <p className="text-sm text-red-700 mt-0.5">{signupError}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Account Type Selection - Reduced Size */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Select Your Account Type
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => handleRoleChange('buyer')}
                  disabled={loading}
                  className={`
                    relative flex items-center justify-center p-3 border rounded-lg transition-all duration-200 disabled:opacity-50 flex-1
                    ${role === 'buyer'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                    }
                  `}
                >
                  <Building className="h-5 w-5 mr-2" />
                  <div className="text-left">
                    <span className="font-semibold block">Buyer</span>
                    <span className="text-xs text-gray-500">
                      Purchase products and services
                    </span>
                  </div>
                  {role === 'buyer' && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    </div>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => handleRoleChange('seller')}
                  disabled={loading}
                  className={`
                    relative flex items-center justify-center p-3 border rounded-lg transition-all duration-200 disabled:opacity-50 flex-1
                    ${role === 'seller'
                      ? 'border-green-500 bg-green-50 text-green-700 shadow-sm'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                    }
                  `}
                >
                  <Briefcase className="h-5 w-5 mr-2" />
                  <div className="text-left">
                    <span className="font-semibold block">Seller</span>
                    <span className="text-xs text-gray-500">
                      Sell products and services
                    </span>
                  </div>
                  {role === 'seller' && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    </div>
                  )}
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Personal Information Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    label="Full Name"
                    {...register('name', { required: 'Full name is required' })}
                    error={errors.name?.message}
                    leftIcon={<User className="h-4 w-4 text-gray-400" />}
                    placeholder="Enter your full name"
                    disabled={loading}
                    size="small"
                  />

                  <Input
                    label="Email Address"
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /\S+@\S+\.\S+/,
                        message: 'Please enter a valid email address'
                      }
                    })}
                    error={errors.email?.message}
                    leftIcon={<Mail className="h-4 w-4 text-gray-400" />}
                    placeholder="you@company.com"
                    disabled={loading}
                    size="small"
                  />

                  <Input
                    label="Phone Number"
                    type="tel"
                    {...register('phone', { required: 'Phone number is required' })}
                    error={errors.phone?.message}
                    leftIcon={<Phone className="h-4 w-4 text-gray-400" />}
                    placeholder="+1 (555) 123-4567"
                    disabled={loading}
                    size="small"
                  />

                  <Select
                    label="Country"
                    {...register('country', { required: 'Country is required' })}
                    error={errors.country?.message}
                    options={countries.map(c => ({ value: c.name, label: c.name }))}
                    leftIcon={<Globe className="h-4 w-4 text-gray-400" />}
                    onChange={(e) => {
                      const country = countries.find(c => c.name === e.target.value);
                      if (country) {
                        setValue('countryCode', country.dialCode);
                      }
                    }}
                    disabled={loading}
                    size="small"
                  />

                  <Input
                    label="State/Province"
                    {...register('state', { required: 'State/Province is required' })}
                    error={errors.state?.message}
                    leftIcon={<MapPin className="h-4 w-4 text-gray-400" />}
                    placeholder="Enter your state or province"
                    disabled={loading}
                    size="small"
                  />

                  <Input
                    label="City"
                    {...register('city', { required: 'City is required' })}
                    error={errors.city?.message}
                    leftIcon={<MapPin className="h-4 w-4 text-gray-400" />}
                    placeholder="Enter your city"
                    disabled={loading}
                    size="small"
                  />

                  <Input
                    label="ZIP/Postal Code"
                    {...register('postalCode', { required: 'Postal code is required' })}
                    error={errors.postalCode?.message}
                    placeholder="Enter your postal code"
                    disabled={loading}
                    size="small"
                  />

                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <textarea
                      {...register('address', { required: 'Address is required' })}
                      rows="2"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50"
                      placeholder="Enter your complete address"
                      disabled={loading}
                    />
                    {errors.address && (
                      <p className="mt-1 text-xs text-red-600 flex items-center">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        {errors.address.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Seller Specific Fields */}
              {showSellerFields && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                    Business Information
                  </h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        label="Company Name"
                        {...register('companyName', {
                          required: 'Company name is required'
                        })}
                        error={errors.companyName?.message}
                        leftIcon={<Building className="h-4 w-4 text-gray-400" />}
                        placeholder="Enter your company name"
                        disabled={loading}
                        size="small"
                      />

                      <Select
                        label="Business Type"
                        {...register('businessType', {
                          required: 'Business type is required'
                        })}
                        error={errors.businessType?.message}
                        options={businessTypes.map(bt => ({ value: bt.name, label: bt.name }))}
                        placeholder="Select your business type"
                        disabled={loading}
                        size="small"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Company Description
                      </label>
                      <textarea
                        {...register('companyDescription', {
                          required: 'Company description is required',
                          minLength: { value: 50, message: 'Minimum 50 characters required' }
                        })}
                        rows="3"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50"
                        placeholder="Describe your company, products, services, and business focus..."
                        disabled={loading}
                      />
                      {errors.companyDescription && (
                        <p className="mt-1 text-xs text-red-600 flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {errors.companyDescription.message}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        label="GST/VAT Number"
                        {...register('GSTNumber')}
                        placeholder="Enter GST/VAT number"
                        disabled={loading}
                        size="small"
                      />

                      <Input
                        label="Business License Number"
                        {...register('licenseNumber')}
                        placeholder="Enter business license number"
                        disabled={loading}
                        size="small"
                      />
                    </div>

                    {/* Company Logo and License Upload - Side by Side */}
                    {/* <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Company Logo (Optional)
                          </label>
                          <label className="cursor-pointer group block">
                            <div className="w-full border-2 border-dashed border-gray-300 rounded-xl group-hover:border-blue-500 transition-colors overflow-hidden bg-gray-50">
                              {companyLogo ? (
                                <div className="relative">
                                  <img
                                    src={URL.createObjectURL(companyLogo)}
                                    alt="Company logo"
                                    className="w-full h-32 object-contain p-3"
                                  />
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      removeFile('logo');
                                    }}
                                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                                  >
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ) : (
                                <div className="p-4 text-center">
                                  <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                                  <p className="text-xs font-medium text-gray-600 mb-0.5">Upload Logo</p>
                                  <p className="text-xs text-gray-500">400×400px, Max 2MB</p>
                                </div>
                              )}
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e, 'logo')}
                              className="hidden"
                              disabled={loading}
                            />
                          </label>
                          {uploadProgress.logo && (
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-700">
                                  Uploading...
                                </span>
                                <span className="text-xs text-gray-600">
                                  {uploadProgress.logo.progress}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                  className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                  style={{ width: `${uploadProgress.logo.progress}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Business License (Optional)
                          </label>
                          <label className="cursor-pointer group block">
                            <div className="w-full border-2 border-dashed border-gray-300 rounded-xl group-hover:border-blue-500 transition-colors overflow-hidden bg-gray-50">
                              {licenseImage ? (
                                <div className="relative">
                                  <img
                                    src={URL.createObjectURL(licenseImage)}
                                    alt="Business license"
                                    className="w-full h-32 object-contain p-3"
                                  />
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      removeFile('license');
                                    }}
                                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                                  >
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ) : (
                                <div className="p-4 text-center">
                                  <Shield className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                                  <p className="text-xs font-medium text-gray-600 mb-0.5">Upload License</p>
                                  <p className="text-xs text-gray-500">Max 5MB</p>
                                </div>
                              )}
                            </div>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => handleFileUpload(e, 'license')}
                              className="hidden"
                              disabled={loading}
                            />
                          </label>
                          {uploadProgress.license && (
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-700">
                                  Uploading...
                                </span>
                                <span className="text-xs text-gray-600">
                                  {uploadProgress.license.progress}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                  className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                  style={{ width: `${uploadProgress.license.progress}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center text-xs text-gray-500">
                        <AlertCircle className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span>These files help verify your business and build trust with partners</span>
                      </div>
                    </div> */}
                  </div>
                </div>
              )}

              {/* Security Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                  Security
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Input
                      label="Password"
                      type="password"
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters'
                        },
                        pattern: {
                          value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                          message: 'Must include uppercase, lowercase, and number'
                        }
                      })}
                      error={errors.password?.message}
                      leftIcon={<Lock className="h-4 w-4 text-gray-400" />}
                      placeholder="Create a strong password"
                      disabled={loading}
                      size="small"
                    />
                  </div>

                  <div>
                    <Input
                      label="Confirm Password"
                      type="password"
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: value => value === password || 'Passwords do not match'
                      })}
                      error={errors.confirmPassword?.message}
                      leftIcon={<Lock className="h-4 w-4 text-gray-400" />}
                      placeholder="Re-enter your password"
                      disabled={loading}
                      size="small"
                    />
                  </div>
                </div>

                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-semibold text-blue-800 mb-1">Password Requirements:</p>
                  <div className="grid grid-cols-2 gap-1">
                    <div className="flex items-center">
                      <CheckCircle className={`h-3 w-3 mr-1 ${password.length >= 8 ? 'text-green-500' : 'text-gray-400'}`} />
                      <span className={`text-xs ${password.length >= 8 ? 'text-green-700' : 'text-gray-600'}`}>
                        Minimum 8 characters
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className={`h-3 w-3 mr-1 ${/[A-Z]/.test(password) ? 'text-green-500' : 'text-gray-400'}`} />
                      <span className={`text-xs ${/[A-Z]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>
                        One uppercase letter
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className={`h-3 w-3 mr-1 ${/[a-z]/.test(password) ? 'text-green-500' : 'text-gray-400'}`} />
                      <span className={`text-xs ${/[a-z]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>
                        One lowercase letter
                      </span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className={`h-3 w-3 mr-1 ${/\d/.test(password) ? 'text-green-500' : 'text-gray-400'}`} />
                      <span className={`text-xs ${/\d/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>
                        One number
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-start">
                  <input
                    id="terms"
                    type="checkbox"
                    {...register('terms', { required: 'You must accept the terms and conditions' })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5 disabled:opacity-50"
                    disabled={loading}
                  />
                  <div className="ml-2">
                    <label htmlFor="terms" className="text-xs text-gray-700">
                      I agree to the{' '}
                      <a href="/terms" className="text-blue-600 hover:text-blue-500 font-medium">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="/privacy" className="text-blue-600 hover:text-blue-500 font-medium">
                        Privacy Policy
                      </a>
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      By creating an account, you agree to receive important notifications.
                    </p>
                  </div>
                </div>
                {errors.terms && (
                  <p className="mt-1 text-xs text-red-600 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {errors.terms.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-3">
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                  loading={loading}
                  className="py-2.5 text-sm font-semibold shadow-sm hover:shadow transition-all duration-200"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating Account...
                    </span>
                  ) : `Create ${role.charAt(0).toUpperCase() + role.slice(1)} Account`}
                </Button>

                <div className="mt-3 text-center">
                  <p className="text-xs text-gray-600">
                    Already have an account?{' '}
                    <a href="/login" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                      Sign in here
                    </a>
                  </p>
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              Your information is protected by SSL encryption.
            </p>
          </div>
        </div>
      </div>
    );
  };

  export default SignupPage;